import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Download, CalendarCheck, ArrowRight, Filter } from "lucide-react";
import api from "../lib/api";
import { formatPriceRange } from "../lib/format";
import { useApp } from "../context/AppContext";
import SectionHeading from "../components/SectionHeading";

const STATUSES = ["All", "New Launch", "Under Construction", "Nearing Possession", "Ready to Move"];

export default function Projects() {
  const { openLead } = useApp();
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState("All");
  useEffect(() => { api.projects().then(setProjects).catch(() => {}); }, []);
  const filtered = projects.filter((p) => status === "All" || p.status === status);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <SectionHeading eyebrow="New Projects" title="New & Upcoming Projects"
        subtitle="Explore flagship developments from trusted builders across India." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`project-status-${s.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${status === s ? "bg-pbs-purple text-white border-pbs-purple" : "bg-white text-pbs-grey border-pbs-line hover:border-pbs-purple"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((pr) => (
          <div key={pr.slug} data-testid={`project-${pr.slug}`} className="bg-white rounded-2xl border border-pbs-line overflow-hidden pbs-shadow hover:pbs-shadow-hover transition-shadow group">
            <div className="relative aspect-[16/10] overflow-hidden">
              <img src={pr.image} alt={pr.name} loading="lazy" className="w-full h-full object-cover zoom-img" />
              <span className="absolute top-3 left-3 bg-pbs-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-md">{pr.status}</span>
            </div>
            <div className="p-5">
              <p className="text-[11px] font-semibold text-pbs-grey">{pr.developer}</p>
              <h3 className="font-bold text-lg text-pbs-dark">{pr.name}</h3>
              <p className="flex items-center gap-1 text-sm text-pbs-grey mt-0.5"><MapPin size={13} className="text-pbs-purple" /> {pr.locality}, {pr.city}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <KV label="Configuration" value={pr.configuration} />
                <KV label="Price" value={formatPriceRange(pr.price_min, pr.price_max)} />
                <KV label="Possession" value={pr.possession} />
                <KV label="RERA" value={pr.rera_id} />
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => openLead({ type: "enquiry", title: `Project: ${pr.name}` })} className="flex-1 text-center text-sm font-bold text-white rounded-lg py-2 pbs-gradient">View Project</button>
                <button onClick={() => openLead({ type: "brochure", title: `Brochure: ${pr.name}` })} className="p-2.5 rounded-lg border border-pbs-line text-pbs-purple hover:bg-pbs-purple/5"><Download size={16} /></button>
                <button onClick={() => openLead({ type: "visit", title: `Visit: ${pr.name}` })} className="p-2.5 rounded-lg border border-pbs-line text-pbs-purple hover:bg-pbs-purple/5"><CalendarCheck size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
const KV = ({ label, value }) => (<div><p className="text-[11px] text-pbs-grey">{label}</p><p className="font-semibold text-pbs-dark text-sm leading-tight">{value}</p></div>);
